/**
 * Detects when a saved Report Builder draft .docx has lost its embedded
 * media in a previous lossy save round-trip.
 *
 * Why this exists: `@eigenpal/docx-editor-react`'s `save()` strips the
 * binaries in `word/media/` while keeping the `<w:drawing>` references
 * in `word/document.xml`. Once such a blob is uploaded as the draft, a
 * future refresh loads a doc that *claims* to have images but renders
 * empty `<img>` elements (the bytes are gone).
 *
 * `docxBufferIsMissingMedia` returns true exactly when the buffer is in
 * that broken state. Callers (e.g. `useTemplateRawDocxBuffer`) use it to
 * fall back to the original template binary so the user still sees their
 * signature / letterhead.
 */
import JSZip from "jszip";

/** Cheap signal that `word/document.xml` references an embedded picture.
 *  Either `<w:drawing>` (DrawingML) or `<w:pict>` (legacy VML) is enough
 *  — both render as a broken `<img>` when their backing media is gone. */
const PICTURE_REFERENCE_PATTERN = /<w:drawing\b|<w:pict\b/;

/** Returns true when the document XML contains an embedded picture
 *  reference (DrawingML `<w:drawing>` or legacy VML `<w:pict>`). The
 *  "document.xml missing from zip" case is handled in the caller, not
 *  here — this helper only inspects the XML string. */
export const documentReferencesPicture = (documentXml: string): boolean =>
  PICTURE_REFERENCE_PATTERN.test(documentXml);

/** True when the .docx claims to have embedded media but the zip has no
 *  `word/media/` entries — i.e. the saved blob is the broken-after-save
 *  variant we want to discard.
 *
 *  Limitation: this catches the "all binaries dropped" shape, not partial
 *  loss. If the library ever drops some images while keeping others
 *  (signature gone, header logo intact), `mediaFiles.length === 0` is
 *  false and we'll still load the broken draft. A tighter check would
 *  parse `word/_rels/document.xml.rels`, enumerate every image rId
 *  referenced from `document.xml`, and confirm each maps to a file in
 *  `word/media/`. Starting with the simple shape because the reported
 *  behaviour is all-or-nothing; widen if QA sees a draft where only
 *  some images survived.
 *
 *  Deliberately swallows IO / parse errors and returns false — a
 *  transient fetch hiccup or malformed zip shouldn't silently discard
 *  the user's saved draft. A blob with a corrupted central directory
 *  won't trigger the heal here, but it would also fail downstream when
 *  the editor tried to parse it, so the user sees a real error instead
 *  of a confusing silent fallback. */
export const docxBufferIsMissingMedia = async (
  buffer: ArrayBuffer
): Promise<boolean> => {
  try {
    const zip = await JSZip.loadAsync(buffer);
    const docEntry = zip.file("word/document.xml");
    if (!docEntry) return false;
    const documentXml = await docEntry.async("string");
    if (!documentReferencesPicture(documentXml)) return false;
    // Any file under word/media/ — png/jpeg/emf/wmf etc — counts as media
    // present. Glob match handles arbitrary nested folders inside media/.
    const mediaFiles = zip.file(/^word\/media\/.+/);
    return mediaFiles.length === 0;
  } catch {
    return false;
  }
};
